import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  MoreVertical,
  Info,
  Search,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { fieldService } from "../services/fieldService";
import datasetService from "../services/datasetService";
import { projectService } from "../services/projectService";
import { useAuth } from "../context/AuthContext";
import { getStoredClientId } from "../utils/tokenUtils";
import toast from "react-hot-toast";

const FIELD_TYPES = [
  { value: "string", label: "String" },
  { value: "date", label: "Date" },
  { value: "textarea", label: "Text Area" },
  { value: "toggle", label: "Toggle (Boolean)" },
  { value: "url", label: "URL" },
  { value: "user", label: "User" },
  { value: "integer", label: "Integer" },
  { value: "dropdown", label: "Dropdown" },
  { value: "multiselect", label: "Multi-select Dropdown" },
];

const CreateFieldPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const storedClientId = getStoredClientId();
  const urlClientId = searchParams.get("client_id");
  const effectiveClientId = urlClientId || storedClientId || user?.client_id;

  const [fieldType, setFieldType] = useState("string");
  const [fieldName, setFieldName] = useState("");
  const [placeholderText, setPlaceholderText] = useState("");
  const [isMandatory, setIsMandatory] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [applyToAllFuture, setApplyToAllFuture] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Dataset state for dropdown/multiselect
  const [datasets, setDatasets] = useState([]);
  const [expandedDatasets, setExpandedDatasets] = useState({});
  const [selectedDataset, setSelectedDataset] = useState(null);

  useEffect(() => {
    if (effectiveClientId) {
      loadProjects();
    }
  }, [effectiveClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects({
        client_id: effectiveClientId,
        page: 1,
        limit: 1000,
        is_active: true,
        is_deleted: false,
      });
      if (response.success && response.data?.projects) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error("Error loading projects:", error);
      toast.error("Failed to load projects");
    }
  };

  const handleAddDataset = () => {
    const datasetNumber = datasets.length + 1;
    const newDataset = {
      id: `temp-${Date.now()}`,
      name: `Dataset #${datasetNumber}`,
      values: [],
      project_ids: [],
      apply_to_all_future_projects: false,
    };
    setDatasets([...datasets, newDataset]);
    setExpandedDatasets({ ...expandedDatasets, [newDataset.id]: true });
  };

  const handleDeleteDataset = (datasetId) => {
    setDatasets(datasets.filter((d) => d.id !== datasetId));
    const newExpanded = { ...expandedDatasets };
    delete newExpanded[datasetId];
    setExpandedDatasets(newExpanded);
  };

  const handleAddValue = (datasetId, valueName) => {
    if (!valueName.trim()) {
      toast.error("Value name is required");
      return;
    }

    const updatedDatasets = datasets.map((dataset) => {
      if (dataset.id === datasetId) {
        const newValue = {
          id: `temp-value-${Date.now()}-${Math.random()}`,
          value_name: valueName.trim(),
          is_default: false,
          position: dataset.values.length,
        };
        return {
          ...dataset,
          values: [...dataset.values, newValue],
        };
      }
      return dataset;
    });
    setDatasets(updatedDatasets);
  };

  const handleToggleDefaultValue = (datasetId, valueId) => {
    const updatedDatasets = datasets.map((dataset) => {
      if (dataset.id === datasetId) {
        const updatedValues = dataset.values.map((value) => {
          if (value.id === valueId) {
            const newIsDefault = !value.is_default;
            // Unset other defaults in this dataset
            if (newIsDefault) {
              return { ...value, is_default: true };
            } else {
              return { ...value, is_default: false };
            }
          } else {
            // Unset other defaults when one is set
            if (
              value.is_default &&
              dataset.values.find((v) => v.id === valueId)?.is_default !== true
            ) {
              return { ...value, is_default: false };
            }
            return value;
          }
        });
        return { ...dataset, values: updatedValues };
      }
      return dataset;
    });
    setDatasets(updatedDatasets);
  };

  const handleDeleteValue = (datasetId, valueId) => {
    const updatedDatasets = datasets.map((dataset) => {
      if (dataset.id === datasetId) {
        const value = dataset.values.find((v) => v.id === valueId);
        if (value?.is_default) {
          toast.error("Cannot delete default value");
          return dataset;
        }
        return {
          ...dataset,
          values: dataset.values
            .filter((v) => v.id !== valueId)
            .map((v, i) => ({ ...v, position: i })),
        };
      }
      return dataset;
    });
    setDatasets(updatedDatasets);
  };

  const handleEditValue = (datasetId, valueId, newName) => {
    const updatedDatasets = datasets.map((dataset) => {
      if (dataset.id === datasetId) {
        return {
          ...dataset,
          values: dataset.values.map((v) =>
            v.id === valueId ? { ...v, value_name: newName } : v,
          ),
        };
      }
      return dataset;
    });
    setDatasets(updatedDatasets);
  };

  const handleProjectToggle = (projectId) => {
    setSelectedProjects((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId],
    );
  };

  const handleSelectAllProjects = () => {
    const filteredProjects = getFilteredProjects();
    if (selectedProjects.length === filteredProjects.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(filteredProjects.map((p) => p.project_id));
    }
  };

  const getFilteredProjects = () => {
    if (!projectSearchQuery.trim()) {
      return projects;
    }
    const query = projectSearchQuery.toLowerCase();
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.project_id?.toLowerCase().includes(query),
    );
  };

  const handleCreateField = async () => {
    if (!fieldName.trim()) {
      toast.error("Field name is required");
      return;
    }

    // For dropdown/multiselect, require at least one dataset with values
    if (
      (fieldType === "dropdown" || fieldType === "multiselect") &&
      datasets.length === 0
    ) {
      toast.error(
        "Please create at least one dataset with values for dropdown fields",
      );
      return;
    }

    // Check each dataset has at least one value
    if (fieldType === "dropdown" || fieldType === "multiselect") {
      for (const dataset of datasets) {
        if (dataset.values.length === 0) {
          toast.error(`Dataset "${dataset.name}" must have at least one value`);
          return;
        }
      }
    }

    try {
      setLoading(true);

      // Create field first
      const fieldData = {
        name: fieldName.trim(),
        type: fieldType,
        is_mandatory: isMandatory,
        placeholder_text: placeholderText.trim() || null,
        project_ids: selectedProjects,
        client_id: effectiveClientId,
      };

      const fieldResponse = await fieldService.createField(fieldData);

      if (!fieldResponse.success) {
        throw new Error(fieldResponse.message || "Failed to create field");
      }

      const fieldId =
        fieldResponse.data?.field?.field_id || fieldResponse.data?.field_id;

      // If dropdown/multiselect, create datasets
      if (
        (fieldType === "dropdown" || fieldType === "multiselect") &&
        fieldId
      ) {
        for (const dataset of datasets) {
          try {
            const datasetData = {
              field_id: fieldId,
              name: dataset.name,
              values: dataset.values.map((v) => ({
                value_name: v.value_name,
                is_default: v.is_default,
                position: v.position,
              })),
              project_ids: dataset.project_ids || [],
              client_id: effectiveClientId,
            };
            const datasetResponse =
              await datasetService.createDataset(datasetData);
            if (!datasetResponse.success) {
              console.error(
                "Failed to create dataset:",
                datasetResponse.message,
              );
              toast.error(`Failed to create dataset "${dataset.name}"`);
            }
          } catch (error) {
            console.error("Error creating dataset:", error);
            toast.error(`Failed to create dataset "${dataset.name}"`);
          }
        }
      }

      toast.success("Field created successfully");
      navigate("/settings?tab=1");
    } catch (error) {
      console.error("Error creating field:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to create field",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDatasetProjectLink = (dataset, showModal = false) => {
    // Store dataset and open modal
    setSelectedDataset(dataset);
    setShowProjectModal(true);
  };

  const handleUpdateDatasetProjects = () => {
    if (selectedDataset) {
      const updatedDatasets = datasets.map((d) =>
        d.id === selectedDataset.id
          ? { ...d, project_ids: selectedProjects }
          : d,
      );
      setDatasets(updatedDatasets);
    }
    setShowProjectModal(false);
    setSelectedDataset(null);
  };

  const filteredProjects = getFilteredProjects();

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate("/settings?tab=1")}
          className="p-2 text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded-md transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Create Field</h1>
          <p className="text-text-secondary mt-1">
            Define a new custom field for your test cases
          </p>
        </div>
      </div>

      {/* Field Type */}
      <div className="card p-6">
        <div>
          <Label htmlFor="field-type">Type</Label>
          <Select value={fieldType} onValueChange={setFieldType}>
            <SelectTrigger id="field-type" className="mt-1">
              <SelectValue placeholder="Select field type" />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Field Details */}
      <div className="card p-6">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Field Name *
            </label>
            <Input
              type="text"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              placeholder="Enter field name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Placeholder text
            </label>
            <Input
              type="text"
              value={placeholderText}
              onChange={(e) => setPlaceholderText(e.target.value)}
              placeholder="Enter placeholder name"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="is-mandatory"
            checked={isMandatory}
            onCheckedChange={setIsMandatory}
          />
          <Label htmlFor="is-mandatory" className="cursor-pointer">
            Mark this field as required
          </Label>
        </div>
      </div>

      {/* Dataset Section for Dropdown/Multiselect */}
      {(fieldType === "dropdown" || fieldType === "multiselect") && (
        <div className="card p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-text-primary">Datasets</h3>
            <button
              onClick={handleAddDataset}
              disabled={datasets.length >= 30}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span>Add New Dataset</span>
            </button>
          </div>

          {datasets.length === 0 && (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-text-secondary mb-4">
                No datasets created yet
              </p>
              <p className="text-sm text-text-tertiary mb-4">
                Create at least one dataset with values to use this dropdown
                field.
              </p>
            </div>
          )}

          {datasets.map((dataset, datasetIndex) => (
            <div
              key={dataset.id}
              className="border border-border-primary rounded-lg p-4 space-y-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <h4 className="font-medium text-text-primary">
                    {dataset.name}
                  </h4>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Dataset information"
                  >
                    <Info className="w-4 h-4 text-text-tertiary" />
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteDataset(dataset.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <p className="text-sm text-text-secondary">
                Values within this dropdown: {dataset.values.length}
              </p>

              {dataset.values.length > 0 && (
                <div className="border border-border-primary rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-bg-secondary">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">
                          TITLE
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">
                          DEFAULT
                        </th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">
                          ACTIONS
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {dataset.values.map((value) => (
                        <tr
                          key={value.id}
                          className="border-t border-border-primary"
                        >
                          <td className="px-4 py-2 text-sm">
                            {value.value_name}
                          </td>
                          <td className="px-4 py-2">
                            <Switch
                              checked={value.is_default}
                              onCheckedChange={() =>
                                handleToggleDefaultValue(dataset.id, value.id)
                              }
                            />
                          </td>
                          <td className="px-4 py-2">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedDataset(dataset);
                                  }}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem
                                  onClick={() => {
                                    const newName = prompt(
                                      "Enter new value name:",
                                      value.value_name,
                                    );
                                    if (newName && newName.trim()) {
                                      handleEditValue(
                                        dataset.id,
                                        value.id,
                                        newName.trim(),
                                      );
                                    }
                                    setSelectedDataset(null);
                                  }}
                                >
                                  Edit Value
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    handleDeleteValue(dataset.id, value.id);
                                    setSelectedDataset(null);
                                  }}
                                  disabled={value.is_default}
                                  className="text-red-600"
                                >
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {dataset.values.length === 0 && (
                <p className="text-sm text-text-tertiary italic">
                  No values added yet. Enter name and click on Add Value to
                  proceed.
                </p>
              )}

              <div className="flex items-center space-x-2">
                <Input
                  type="text"
                  placeholder="Enter value name"
                  className="flex-1"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAddValue(dataset.id, e.target.value);
                      e.target.value = "";
                    }
                  }}
                />
                <Button
                  onClick={(e) => {
                    const input = e.target.previousElementSibling;
                    handleAddValue(dataset.id, input.value);
                    input.value = "";
                  }}
                >
                  Add Value
                </Button>
              </div>

              {/* Link Dataset to Project */}
              <div className="border-t border-border-primary pt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-text-primary">
                      Link Dataset to Project
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedProjects(dataset.project_ids || []);
                      handleDatasetProjectLink(dataset);
                    }}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Update
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {dataset.project_ids?.length || 0} Projects linked
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Link Field to Projects (for non-dropdown fields) */}
      {fieldType !== "dropdown" && fieldType !== "multiselect" && (
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-text-primary mb-1">
                Link Field to Projects
              </h3>
              <p className="text-sm text-text-secondary">
                {selectedProjects.length}{" "}
                {selectedProjects.length === 1 ? "Project" : "Projects"}{" "}
                selected
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedDataset(null); // Not a dataset, just field linking
                setShowProjectModal(true);
              }}
              className="btn-primary px-4 py-2"
            >
              Update
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        <Button variant="outline" onClick={() => navigate("/settings?tab=1")}>
          Cancel
        </Button>
        <Button
          onClick={handleCreateField}
          disabled={loading || !fieldName.trim()}
        >
          {loading ? "Creating..." : "Create"}
        </Button>
      </div>

      {/* Value Menu - Now handled inline in DropdownMenu above */}

      {/* Project Selection Modal */}
      <Dialog
        open={showProjectModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowProjectModal(false);
            setSelectedDataset(null);
            setProjectSearchQuery("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedDataset
                ? `Link Dataset to Projects`
                : `Link Field to Projects`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="apply-to-all-future"
                checked={applyToAllFuture}
                onCheckedChange={setApplyToAllFuture}
              />
              <Label htmlFor="apply-to-all-future" className="cursor-pointer">
                Apply to All Future Projects
              </Label>
            </div>
            {applyToAllFuture && (
              <p className="text-sm text-text-tertiary ml-10">
                This option allows you to use this{" "}
                {selectedDataset ? "dataset" : "field"} for all future projects.
              </p>
            )}

            <div className="relative">
              <Input
                type="text"
                placeholder="Search projects by title/ID"
                value={projectSearchQuery}
                onChange={(e) => setProjectSearchQuery(e.target.value)}
                className="pl-10"
              />
              <Search className="absolute left-3 top-3 w-4 h-4 text-text-tertiary" />
            </div>

            <div className="border border-border-primary rounded-lg max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-bg-secondary sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left">
                      <Checkbox
                        checked={
                          filteredProjects.length > 0 &&
                          filteredProjects.every((p) =>
                            selectedProjects.includes(p.project_id),
                          )
                        }
                        onCheckedChange={handleSelectAllProjects}
                      />
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">
                      ID
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">
                      TITLE
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => (
                    <tr key={project.project_id} className="hover:bg-bg-hover">
                      <td className="px-4 py-2">
                        <Checkbox
                          checked={selectedProjects.includes(
                            project.project_id,
                          )}
                          onCheckedChange={() =>
                            handleProjectToggle(project.project_id)
                          }
                        />
                      </td>
                      <td className="px-4 py-2 text-sm text-text-primary">
                        {project.project_id?.substring(0, 8)}...
                      </td>
                      <td className="px-4 py-2 text-sm">{project.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowProjectModal(false);
                setSelectedDataset(null);
                setProjectSearchQuery("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedDataset) {
                  handleUpdateDatasetProjects();
                } else {
                  // For regular field linking
                  setShowProjectModal(false);
                }
              }}
            >
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateFieldPage;
